import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { receiptsAPI, billsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import { DocHeader, DocFooter } from '../components/DocHeader';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const METHOD_LABELS = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', online: 'Online', other: 'Other' };

export default function ReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideHeaderFooter, setHideHeaderFooter] = useState(false);
  const [billData, setBillData] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const printRef = useRef();

  const cur = settings.currency || 'BDT';
  const primary = settings.primary_color || '#2563eb';

  useEffect(() => {
    receiptsAPI.getOne(id)
      .then(res => setReceipt(res.data))
      .catch(() => toast.error('Receipt not found'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
  if (!receipt?.bill_id) return;
  billsAPI.getOne(receipt.bill_id)
    .then(res => {
      setBillData(res.data);
      setBillItems(res.data.items || []);
    })
    .catch(() => {});

  // Get all receipts for this bill BEFORE this one (by id)
  receiptsAPI.getAll({ limit: 100 })
    .then(res => {
      const allPayments = res.data.receipts.filter(
        r => String(r.bill_id) === String(receipt.bill_id) && r.id < receipt.id
      );
      const paid = allPayments.reduce((s, r) => s + parseFloat(r.amount), 0);
      setPreviouslyPaid(paid);
    })
    .catch(() => {});
}, [receipt]);

 const [previouslyPaid, setPreviouslyPaid] = useState(0);

const totalPayable = billData ? parseFloat(billData.total) - previouslyPaid : 0;
const totalReceived = parseFloat(receipt?.amount || 0);
const totalDue = totalPayable - totalReceived;

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: receipt ? `Receipt-${receipt.receipt_number}-${receipt.client_name}` : 'Receipt',
    onAfterPrint: () => setHideHeaderFooter(false),
    pageStyle: `
      @page { size: A4; margin: 0; }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        body { margin: 0; padding: 0; }
      }
    `,
  });

  const handleDelete = async () => {
    if (!window.confirm(`Delete receipt ${receipt.receipt_number}?`)) return;
    try {
      await receiptsAPI.delete(id);
      toast.success('Receipt deleted');
      navigate('/receipts');
    } catch { toast.error('Delete failed'); }
  };

  const fmtDate = d => d ? format(new Date(d), 'dd.MM.yyyy') : '-';

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  if (!receipt) return <div style={{ padding: 40 }}>Receipt not found. <Link to="/receipts">Go back</Link></div>;

  const emptyRows = Math.max(0, 4 - (billItems.length > 0 ? billItems.length : 1));

  return (
    <div className="doc-wrapper">
      <Link to="/receipts" className="back-link">← Back to Receipts</Link>

      <div className="page-actions">
        <button className="btn btn-outline" onClick={handlePrint}>🖨 Print / PDF</button>
        <button className="btn btn-outline" onClick={() => { setHideHeaderFooter(true); setTimeout(handlePrint, 100); }}>🖨 No Header/Footer</button>
        <button className="btn btn-danger" onClick={handleDelete}>🗑 Delete</button>
      </div>

      {/* Printable Document */}
      <div ref={printRef}>
        <div style={{
          background: '#fff',
          width: '100%',
          minHeight: '297mm',
          fontFamily: "'DM Sans', sans-serif",
          display: 'flex',
          flexDirection: 'column',
        }}>

          {/* HEADER */}
          {hideHeaderFooter ? (
            <div style={{ height: 120, width: '100%' }} />
          ) : (
            <DocHeader docType="RECEIPT" />
          )}

          {/* RECEIVED FROM + META */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 48px' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', borderBottom: '1.5px solid #e2e8f0', paddingBottom: 4, marginBottom: 8 }}>
                Received From
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{receipt.client_name}</div>
              {receipt.client_address && <div style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{receipt.client_address}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Date:</span>
                <span style={{ fontWeight: 600, color: primary }}>{fmtDate(receipt.payment_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Receipt No.</span>
                <span style={{ fontWeight: 700 }}>{receipt.receipt_number}</span>
              </div>
              {receipt.bill_number && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Bill No.</span>
                  <span style={{ fontWeight: 600 }}>{receipt.bill_number}</span>
                </div>
              )}
              {receipt.payment_method && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Method:</span>
                  <span>{METHOD_LABELS[receipt.payment_method] || receipt.payment_method}</span>
                </div>
              )}
              {receipt.reference_number && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Ref:</span>
                  <span>{receipt.reference_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* ITEMS TABLE */}
          <div style={{ padding: '0 48px 20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <thead style={{ backgroundColor: primary }}>
                <tr style={{ backgroundColor: primary }}>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 60, textAlign: 'center' }}>SL.</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 160, textAlign: 'right' }}>Amount ({cur})</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
                  <td style={{ padding: '13px 16px', textAlign: 'center', color: '#64748b', fontSize: 14 }}>1</td>
                  <td style={{ padding: '13px 16px', fontSize: 14 }}>
                   {receipt.bill_number && (
  <div style={{ fontWeight: 600, marginBottom: 4 }}>
    Payment Received Against {receipt.bill_number}{billData ? ` (${cur} ${parseFloat(billData.total).toLocaleString()})` : ''}

  
  </div>
)}
                    {billItems.length > 0 ? (
                      billItems.map((item, i) => (
                        <div key={i} style={{ color: '#64748b', fontSize: 13 }}>{item.description}</div>
                      ))
                    ) : (
                      <div style={{ color: '#64748b' }}>{receipt.notes || 'Payment received'}</div>
                    )}
                  </td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>
                    {parseFloat(receipt.amount).toLocaleString()}
                  </td>
                </tr>
                {Array.from({ length: emptyRows }).map((_, i) => (
                  <tr key={`e${i}`} style={{ borderBottom: '1px solid #e2e8f0', background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '13px 16px' }}>&nbsp;</td>
                    <td style={{ padding: '13px 16px' }}></td>
                    <td style={{ padding: '13px 16px' }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTALS */}
          <div style={{ padding: '0 48px 28px' }}>
            {billData ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <table style={{ minWidth: 300, borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '7px 16px', fontSize: 14, color: '#64748b' }}>Total Payable</td>
                      <td style={{ padding: '7px 16px', fontSize: 14, textAlign: 'right' }}>
                        {cur} {totalPayable.toLocaleString()}
                      </td>
                    </tr>
                    <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '7px 16px', fontSize: 14, color: '#64748b' }}>Total Received</td>
                      <td style={{ padding: '7px 16px', fontSize: 14, textAlign: 'right', color: '#10b981', fontWeight: 600 }}>
                        {cur} {totalReceived.toLocaleString()}
                      </td>
                    </tr>
                    <tr style={{ borderTop: '2px solid #0f172a' }}>
                      <td style={{ padding: '10px 16px', fontSize: 16, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif' }}>
                        Total Due
                      </td>
                      <td style={{ padding: '10px 16px', fontSize: 16, fontWeight: 800, textAlign: 'right', fontFamily: 'Space Grotesk, sans-serif', color: totalDue <= 0 ? '#10b981' : '#ef4444' }}>
                        {cur} {Math.abs(totalDue).toLocaleString()}
                        {totalDue <= 0 && (
                          <span style={{ fontSize: 11, marginLeft: 8, fontWeight: 700, background: '#10b981', color: '#fff', padding: '2px 8px', borderRadius: 12 }}>
                            PAID
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '2px solid #0f172a', paddingTop: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Total Received</div>
                <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800, fontSize: 18 }}>
                  {cur} {parseFloat(receipt.amount).toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {/* NOTES */}
          {receipt.notes && !billItems.length && (
            <div style={{ padding: '0 48px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>{receipt.notes}</div>
            </div>
          )}

          {/* SPACER */}
          <div style={{ flex: 1 }} />

          {/* FOOTER */}
          {hideHeaderFooter ? (
            <div style={{ height: 80, width: '100%' }} />
          ) : (
            <DocFooter />
          )}

        </div>
      </div>
    </div>
  );
}